import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

const DECISION_TYPES = [
  { value: 'strategic', label: 'Strategic' },
  { value: 'operational', label: 'Operational' },
  { value: 'financial', label: 'Financial' },
  { value: 'personal', label: 'Personal' },
  { value: 'other', label: 'Other' },
];

export default function EditDecisionDialog({ open, onOpenChange, decision, onUpdated }) {
  const { getAuthHeaders, API } = useAuth();
  const [title, setTitle] = useState('');
  const [context, setContext] = useState('');
  const [date, setDate] = useState(new Date());
  const [decisionType, setDecisionType] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (decision) {
      setTitle(decision.title);
      setContext(decision.context);
      setDecisionType(decision.decision_type || '');
      try {
        setDate(parseISO(decision.date));
      } catch {
        setDate(new Date());
      }
    }
  }, [decision]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(
        `${API}/decisions/${decision.id}`,
        {
          title: title.trim(),
          context: context.trim(),
          date: format(date, 'yyyy-MM-dd'),
          decision_type: decisionType || null
        },
        { headers: getAuthHeaders() }
      );
      
      onUpdated(response.data);
      onOpenChange(false);
      toast.success('Decision updated');
    } catch (error) {
      console.error('Error updating decision:', error);
      toast.error('Failed to update decision');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-decision-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-medium">Edit Decision</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="edit-title" className="text-sm tracking-wide text-muted-foreground uppercase">
              Title
            </Label>
            <Input
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What did you decide?"
              className="input-underline text-lg py-3"
              data-testid="edit-decision-title-input"
            />
          </div>

          {/* Context */}
          <div className="space-y-2">
            <Label htmlFor="edit-context" className="text-sm tracking-wide text-muted-foreground uppercase">
              Context
            </Label>
            <Textarea
              id="edit-context"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Why did you make this decision?"
              className="min-h-[100px] resize-none bg-transparent border border-input rounded-lg focus-visible:ring-1 focus-visible:ring-ring"
              data-testid="edit-decision-context-input"
            />
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label className="text-sm tracking-wide text-muted-foreground uppercase">
              Date
            </Label>
            <Popover modal={true}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !date && "text-muted-foreground"
                  )}
                  data-testid="edit-decision-date-picker"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, 'PPP') : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 z-[100]" align="start" sideOffset={4}>
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => d && setDate(d)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Decision Type (Optional) */}
          <div className="space-y-2">
            <Label className="text-sm tracking-wide text-muted-foreground uppercase">
              Type <span className="text-muted-foreground/50 normal-case">(optional)</span>
            </Label>
            <Select value={decisionType} onValueChange={setDecisionType}>
              <SelectTrigger data-testid="edit-decision-type-select">
                <SelectValue placeholder="Select type..." />
              </SelectTrigger>
              <SelectContent>
                {DECISION_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid="cancel-edit-decision"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="rounded-sm"
              data-testid="submit-edit-decision"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
